import React, { useRef, useCallback, useEffect } from 'react';
import { pointsToSvgPath } from './NapkinFormat';

/**
 * DrawMode — SVG overlay for freehand sketching.
 *
 * Renders as an absolutely-positioned SVG that covers the artboard.
 * Pointer events are captured only when `active` is true.
 *
 * Uses DOM refs for the live stroke path so every pointermove does NOT
 * trigger a React re-render, giving smooth 60fps drawing performance.
 * The saved strokes (from `napkin.objects`) are rendered as React elements.
 *
 * Props:
 *   napkin         current napkin object (from NapkinFormat)
 *   active         boolean — whether draw mode is enabled
 *   erasing        boolean — click on a stroke removes it
 *   color          hex string
 *   size           stroke width in px
 *   width / height artboard dimensions (default 1200 / 800)
 *   onStrokeEnd(points) — called with finalized point array
 *   onEraseStroke(strokeId)
 */
const DrawMode = ({
  napkin,
  active = false,
  erasing = false,
  color = '#1a1a1a',
  size = 2,
  width = 1200,
  height = 800,
  onStrokeEnd,
  onEraseStroke,
}) => {
  const svgRef = useRef(null);
  const livePathRef = useRef(null);
  const isDrawingRef = useRef(false);
  const currentPointsRef = useRef([]);

  // ── coordinate helper ─────────────────────────────────────────────────────

  const getSVGPoint = useCallback((e) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const rect = svg.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * width,
      y: ((e.clientY - rect.top) / rect.height) * height,
    };
  }, [width, height]);

  // ── pointer handlers ──────────────────────────────────────────────────────

  const handlePointerDown = useCallback((e) => {
    if (!active || erasing) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    isDrawingRef.current = true;
    const pt = getSVGPoint(e);
    currentPointsRef.current = [pt];

    // Stamp a single dot for single-point strokes
    if (livePathRef.current) {
      livePathRef.current.setAttribute('d', `M ${pt.x},${pt.y} L ${pt.x + 0.01},${pt.y}`);
      livePathRef.current.setAttribute('stroke', color);
      livePathRef.current.setAttribute('stroke-width', String(size));
    }
  }, [active, erasing, color, size, getSVGPoint]);

  const handlePointerMove = useCallback((e) => {
    if (!isDrawingRef.current || !active || erasing) return;
    e.preventDefault();
    const pt = getSVGPoint(e);
    currentPointsRef.current.push(pt);

    if (livePathRef.current) {
      const d = pointsToSvgPath(currentPointsRef.current);
      livePathRef.current.setAttribute('d', d);
    }
  }, [active, erasing, getSVGPoint]);

  const handlePointerUp = useCallback((e) => {
    if (!isDrawingRef.current) return;
    isDrawingRef.current = false;

    const pts = currentPointsRef.current;
    currentPointsRef.current = [];

    // Clear live path
    if (livePathRef.current) {
      livePathRef.current.setAttribute('d', '');
    }

    if (pts.length >= 1) {
      onStrokeEnd?.(pts);
    }
  }, [onStrokeEnd]);

  // ── eraser click ──────────────────────────────────────────────────────────

  const handleClick = useCallback((e) => {
    if (!active || !erasing) return;
    const target = e.target;
    const strokeId = target.getAttribute('data-stroke-id');
    if (strokeId) {
      e.stopPropagation();
      onEraseStroke?.(strokeId);
    }
  }, [active, erasing, onEraseStroke]);

  // ── render ────────────────────────────────────────────────────────────────

  const objects = napkin?.objects || [];

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${width} ${height}`}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: active ? 'all' : 'none',
        cursor: active ? (erasing ? 'cell' : 'crosshair') : 'default',
        touchAction: 'none',
        overflow: 'visible',
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onClick={handleClick}
    >
      {/* Saved strokes */}
      {objects.filter((o) => o.type === 'stroke').map((o) => (
        <path
          key={o.id}
          data-stroke-id={o.id}
          d={pointsToSvgPath(o.data?.points || [])}
          fill="none"
          stroke={o.data?.color || '#1a1a1a'}
          strokeWidth={o.data?.size ?? 2}
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            cursor: active && erasing ? 'pointer' : 'default',
            opacity: 0.9,
          }}
        />
      ))}

      {/* Live stroke being drawn — updated via DOM ref */}
      <path
        ref={livePathRef}
        d=""
        fill="none"
        stroke={color}
        strokeWidth={size}
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity={0.9}
      />
    </svg>
  );
};

export default DrawMode;
